import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom/client';

import { MessageConfig, MessageInstance, MessageOptions } from '../types';

import MessageContainer, { MessageContainerRef } from './messageContainer';

interface InstanceMap {
  [key: string]: {
    div: HTMLDivElement;
    containerRef: React.RefObject<any>;
    config: MessageConfig;
    add: (notice: MessageOptions) => () => void;
    remove: (key: string | number) => void;
    destroy: () => void;
  };
}
const instances: InstanceMap = {};
const defaultConfig: MessageConfig = {
  duration: 3,
  maxCount: 5,
  position: 'topCenter',
};

/**
 * 创建消息实例
 * @param config
 * @returns
 */
const createInstance = (config: MessageConfig = {}) => {
  const instanceId = config.instanceId || 'default';

  // 若实例已存在，直接返回
  if (instances[instanceId]) {
    // 更新配置
    instances[instanceId].config = {
      ...instances[instanceId].config,
      ...config,
    };
    return instances[instanceId];
  }

  /**
   * 创建容器
   */
  const div = document.createElement('div');
  document.body.appendChild(div);

  /**
   * 使用引用存储 MessageContainer 组件的实例
   */

  const messageContainerRef = React.createRef<MessageContainerRef>();

  /**
   * 创建消息根节点
   */
  const root = ReactDOM.createRoot(div);

  /**
   * 创建实例对象
   */
  const instance = {
    div,
    containerRef: messageContainerRef,
    config: { ...defaultConfig, ...config },
    add: (notice: MessageOptions) => {
      // 使用已创建的容器引用
      if (instance.containerRef.current) {
        return instance.containerRef.current.add(notice);
      }

      // 如果容器引用还不可用，尝试延迟添加消息
      return () => { };
    },
    remove: (key: string | number) => {
      if (instance.containerRef.current) {
        instance.containerRef.current.remove(key);
      }
    },
    destroy: () => {
      root.unmount();
      div.remove();
      delete instances[instanceId];
    },
  };

  /**
   * 存储实例
   */
  instances[instanceId] = instance;

  /**
   * 渲染容器组件 - 只在创建实例时渲染一次
   */
  root.render(
    <MessageContainer ref={messageContainerRef} config={instance.config} />,
  );

  return instance;
};

/**
 * 创建一个通知
 * @param options
 * @returns
 */
const notice = (options: MessageOptions) => {
  // 确保 options 是 MessageOptions 类型
  const safeOptions: MessageOptions = { ...options };

  // 如果有 React 元素作为 content，确保不会将其 key 属性传入 MessageOptions
  if (React.isValidElement(safeOptions.content)) {
    throw new Error('Message content should not be a React element.');
  }

  const instanceId = safeOptions.instanceId || 'default';
  const instance = createInstance({ instanceId });
  return instance.add(safeOptions);
};

/**
 * 创建不同类型的通知函数
 * @param type
 * @returns
 */
const createNoticeFunction = (type: string) => {
  return (
    content: React.ReactNode | MessageOptions,
    duration?: number,
    onClose?: (id: string | number) => void,
  ) => {
    if (
      typeof content === 'object' &&
      content !== null &&
      !React.isValidElement(content)
    ) {
      return notice({
        ...(content as object),
        type: type as any,
      } as MessageOptions);
    }
    return notice({
      content,
      type: type as any,
      duration,
      onClose,
    } as MessageOptions);
  };
};

/**
 * 移除指定通知
 * @param key
 * @param instanceId
 */
const removeNotice = (key: string | number, instanceId: string = 'default') => {
  const instance = instances[instanceId];
  if (instance) {
    instance.remove(key);
  }
};

/**
 * 销毁所有通知
 * @param instanceId
 * @returns
 */
const destroyAll = (instanceId?: string) => {
  if (instanceId) {
    const instance = instances[instanceId];
    if (instance) {
      instance.destroy();
    }
    return;
  }

  Object.keys(instances).forEach((key) => {
    instances[key].destroy();
  });
};

/**
 * 更新全局配置
 * @param options
 * @returns
 */
const configMessage = (options: MessageConfig = {}) => {
  const instanceId = options.instanceId || 'default';

  // 检查是否有现有实例
  if (instances[instanceId]) {
    // 更新现有实例的配置
    instances[instanceId].config = {
      ...instances[instanceId].config,
      ...options,
    };

    // 获取 MessageContainer 组件引用，并直接更新其配置
    const containerRef = instances[instanceId].containerRef?.current;
    if (containerRef && typeof containerRef.updateConfig === 'function') {
      containerRef.updateConfig(instances[instanceId].config);
    }

    return;
  }

  // 如果没有现有实例，创建新实例
  createInstance({ instanceId, ...options });
};

/**
 * useMessage
 * @returns
 */
const useMessage = (): [MessageInstance, React.ReactElement] => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const configRef = useRef<MessageConfig>({ ...defaultConfig });

  useEffect(() => {
    if (!containerRef.current) {
      containerRef.current = document.createElement('div');
      document.body.appendChild(containerRef.current);

      return () => {
        if (containerRef.current) {
          containerRef.current.remove();
          containerRef.current = null;
        }
      };
    }
  }, []);

  const messageInstance = useMemo<MessageInstance>(
    () => ({
      notice: createNoticeFunction('notice'),
      info: createNoticeFunction('info'),
      success: createNoticeFunction('success'),
      warning: createNoticeFunction('warning'),
      error: createNoticeFunction('error'),
      loading: createNoticeFunction('loading'),
      open: notice,
      remove: removeNotice,
      destroy: destroyAll,
      config: configMessage,
      useMessage,
    }),
    [],
  );

  const contextHolder = (
    <div style={{ display: 'none' }} id="message-container-context-holder" />
  );

  return [messageInstance, contextHolder];
};

const message: MessageInstance = {
  notice: createNoticeFunction('notice'),
  info: createNoticeFunction('info'),
  success: createNoticeFunction('success'),
  warning: createNoticeFunction('warning'),
  error: createNoticeFunction('error'),
  loading: createNoticeFunction('loading'),
  open: notice,
  remove: removeNotice,
  destroy: destroyAll,
  config: configMessage,
  useMessage,
};

export default message;
